import { useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { Calendar } from "./ui/calendar-rac";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { format } from "date-fns";
import { getLocalTimeZone, today } from "@internationalized/date";
import type { DateValue } from "react-aria-components";
import { LocationSearchInput } from "./location/LocationSearchInput";
import { cn } from "../lib/utils";
import { FloristsMap } from './FloristsMap';

interface HeroProps {
  onSearch: (params: {
    location: string;
    date?: Date;
    budget?: [number, number];
    deliveryType: 'delivery' | 'pickup';
    coordinates?: { lat: number; lng: number };
  }) => void;
}

export function Hero({ onSearch }: HeroProps) {
  const [date, setDate] = useState<DateValue | null>(null);
  const [budget, setBudget] = useState<[number, number]>([0, 500]);
  const [deliveryType, setDeliveryType] = useState<'delivery' | 'pickup'>('delivery');
  const [locationInput, setLocationInput] = useState("");
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  
  const handleSearch = () => {
    onSearch({
      location: locationInput || '',
      date: date?.toDate(getLocalTimeZone()),
      budget,
      deliveryType,
      coordinates: coordinates || undefined
    });
  };

  const formatPriceRange = () => {
    if (budget[1] === 500) return `$${budget[0]} - $500+`;
    return `$${budget[0]} - $${budget[1]}`;
  };

  const handleDateSelect = (value: DateValue | null) => {
    setDate(value);
    setIsCalendarOpen(false);
  };

  const formatDate = (date: DateValue | null) => {
    if (!date) return 'Any Date or Pick a Date';
    return format(date.toDate(getLocalTimeZone()), 'PPP');
  };

  const handleBudgetChange = (value: number[]) => {
    setBudget([value[0], value[1]] as [number, number]);
  };

  const handlePlaceSelected = (place: { formattedAddress: string; coordinates: { lat: number; lng: number } }) => {
    setLocationInput(place.formattedAddress);
    setCoordinates(place.coordinates);
  };

  return (
    <div className="w-full">
      {/* Full-width Hero Background */}
      <div className="relative w-full min-h-[585px] h-auto md:h-[600px] lg:h-[700px] overflow-hidden">
        {/* Preload hero image for faster loading */}
        <link rel="preload" as="image" href="/images/roses-hero.jpg" />
        <div 
          className="absolute inset-0 bg-no-repeat"
          style={{ 
            backgroundImage: 'url(/images/roses-hero.jpg)',
            backgroundSize: 'cover',
            imageRendering: '-webkit-optimize-contrast',
            WebkitBackfaceVisibility: 'hidden',
            transform: window.innerWidth < 768 ? 'scale(1.8) translateZ(0)' : 'scale(1) translateZ(0)',
            transformOrigin: window.innerWidth < 768 ? 'center 50%' : 'center center',
            backgroundPosition: window.innerWidth < 768 ? 'center 50%' : 'center center'
          }}
        >
          {/* Gradient overlay for better text contrast */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
            
        {/* Content Container */}
        <div className="relative z-10 px-4 py-8 sm:py-12 md:py-16 lg:py-20">
          {/* Text Block - Main Heading - Static */}
          <div className="w-full mb-6 text-center">
            <h1 className="font-bold tracking-[-0.02em] text-white satoshi-font text-[6vw] md:text-[5vw] lg:text-[4vw] leading-[0.9]">
              Sydney's Best Local Florists
            </h1>
          </div>
          
          <div className="w-full max-w-[1200px] mx-auto">
              
            {/* Search Bar - Glass morphism with cleaner design */}
            <div className="w-full max-w-[700px] mx-auto bg-white/80 backdrop-blur-md rounded-xl shadow-2xl p-3 sm:p-4 md:p-5 lg:p-6 border border-gray-200">
                <div className="space-y-2 sm:space-y-3">
                  {/* Location Search - Full Width */}
                  <div className="w-full">
                    <label htmlFor="location-input" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 satoshi-font uppercase tracking-wide">Where to?</label>
                    <LocationSearchInput
                      onPlaceSelected={handlePlaceSelected}
                      defaultValue={locationInput}
                      placeholder="Enter suburb or postcode..."
                      className="w-full h-10 sm:h-12 px-3 sm:px-4 py-0 text-sm sm:text-base border-2 border-gray-300 rounded-lg bg-white text-gray-700 placeholder-gray-500 focus:ring-2 focus:ring-[#967F81] focus:border-[#967F81] transition-all satoshi-font flex items-center"
                    />
                  </div>

                  {/* Secondary Options */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                    {/* Date */}
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 satoshi-font uppercase tracking-wide">When?</label>
                      <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            id="date-picker"
                            variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal h-10 sm:h-12 pl-3 sm:pl-4 pr-3 sm:pr-4 py-0 text-sm sm:text-base border-2 rounded-lg bg-white hover:bg-gray-50 focus:bg-white transition-all satoshi-font flex items-center",
                          !date ? "border-gray-300 text-gray-500" : "border-[#967F81] bg-[#967F81]/10 text-gray-700"
                        )}
                          >
                            <CalendarIcon className="mr-1.5 h-4 w-4 text-gray-600 flex-shrink-0" />
                            <span className="truncate">{formatDate(date)}</span>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-white" align="start">
                          <div className="p-3">
                            <Calendar
                              value={date}
                              onChange={handleDateSelect}
                              minValue={today(getLocalTimeZone())}
                            />
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <Button
                                variant="outline"
                                className="w-full text-sm text-[#2C1810] border-[#967F81] bg-[#967F81] hover:bg-[#843329]/10"
                                onClick={() => {
                                  setDate(null);
                                  setIsCalendarOpen(false);
                                }}
                              >
                                Any Date
                              </Button>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Budget */}
                    <div>
                      <label htmlFor="budget-slider" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 satoshi-font uppercase tracking-wide">Budget: {formatPriceRange()}</label>
                      <div className="bg-white border-2 border-gray-300 rounded-lg p-2 sm:p-3 h-10 sm:h-12 flex items-center">
                        <Slider
                          id="budget-slider"
                          value={budget}
                          onValueChange={handleBudgetChange}
                          min={0}
                          max={1000}
                          step={25}
                          className="w-full [&>span:first-child]:h-2 [&>span:first-child]:bg-gray-300 [&>span:first-child>span]:bg-[#967F81] [&_span[role=slider]]:h-5 [&_span[role=slider]]:w-5 [&_span[role=slider]]:bg-[#967F81] [&_span[role=slider]]:border-2 [&_span[role=slider]]:border-white [&_span[role=slider]]:shadow-md"
                        />
                      </div>
                    </div>

                    {/* Delivery Type */}
                    <div>
                      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2 satoshi-font uppercase tracking-wide">Type</label>
                      <div className="flex bg-white border-2 border-gray-300 p-1 rounded-lg h-10 sm:h-12">
                        <Button
                          onClick={() => setDeliveryType('delivery')}
                          variant="ghost"
                          className={`flex-1 h-full text-sm font-medium rounded-full transition-all satoshi-font ${deliveryType === 'delivery' ? 'bg-[#967F81] text-white' : 'bg-transparent text-gray-700 hover:bg-gray-100'}`}
                        >
                          Delivery
                        </Button>
                        <Button
                          onClick={() => setDeliveryType('pickup')}
                          variant="ghost"
                          className={`flex-1 h-full text-sm font-medium rounded-full transition-all satoshi-font ${deliveryType === 'pickup' ? 'bg-[#967F81] text-white' : 'bg-transparent text-gray-700 hover:bg-gray-100'}`}
                        >
                          Pickup
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Search Button - Modern pill shape */}
                  <div className="w-full pt-2">
                    <Button
                      onClick={handleSearch}
                      className="w-full h-10 sm:h-12 text-sm sm:text-base font-semibold bg-[#967F81] hover:bg-[#843329] text-white rounded-full transition-all satoshi-font uppercase tracking-wide shadow-lg hover:shadow-xl"
                      disabled={!locationInput}
                    >
                      Search Florists
                    </Button>
                  </div>
                </div>
              </div>
          </div>

          {/* Static Subtitle Text */}
          <div className="w-full mt-12 mb-8 text-center space-y-1">
            <h2 className="font-bold tracking-[-0.02em] text-white satoshi-font text-[6vw] md:text-[5vw] lg:text-[4vw] leading-[0.9]">
              All in one place
            </h2>
            <h2 className="font-bold tracking-[-0.02em] text-white satoshi-font text-[6vw] md:text-[5vw] lg:text-[4vw] leading-[0.9]">
              Ready to checkout now
            </h2>
          </div>
        </div>
      </div>

      {/* Florists Map Section - Split layout with video */}
      <div className="w-full py-12 sm:py-16 md:py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-left mb-6 sm:mb-8 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-brand-secondary tracking-tight satoshi-font mb-2 sm:mb-3">
              Florists Near You
            </h2>
            <p className="text-neutral-600 text-sm sm:text-[15px] max-w-2xl satoshi-font">
              Discover local florists in your area, ready to create beautiful arrangements
            </p>
          </div>
          
          {/* Split layout: Map on left, Video on right */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            {/* Map Section */}
            <div className="h-[300px] lg:h-[400px] relative">
              <div className="absolute inset-0 rounded-lg overflow-hidden border border-neutral-200">
                <FloristsMap />
              </div>
            </div>
            
            {/* Video Section */}
            <div className="h-[300px] lg:h-[400px] bg-neutral-100 relative rounded-lg overflow-hidden border border-neutral-200">
              <video 
                className="w-full h-full object-cover"
                autoPlay 
                loop 
                muted 
                playsInline
                poster="/images/roses-hero.jpg"
              >
                <source src="/videos/video hero.mp4" type="video/mp4" />
                {/* Fallback image if video doesn't load */}
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
} 